import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IrisDmsLinkedFilesStore } from '@iris/common/modules/dms/store/linked-files.signal-store';
import { TranslatePipe } from '@ngx-translate/core';
import { IrisFilesService } from '@iris/common/modules/dms/services/files.service';
import { IrisDmsPermissionsSandbox } from '@iris/common/modules/dms/sandbox/dms-permissions.sandbox';
import { IrisDmsFileInfoStore } from '@iris/common/modules/dms/store/file-info.signal-store';
import { IrisDmsDownloadFileButtonComponent } from '../../../file-actions/dms-download-file-button/dms-download-file-button.component';
import { IrisDmsUnlinkFileToFileButtonComponent } from '../../../file-actions/dms-unlink-file-to-file-button/dms-unlink-file-to-file-button.component';
import { IrisDmsRoutingService } from '@iris/common/modules/dms/services/dms-routing.service';

@Component({
  selector: 'iris-dms-file-link-card',
  imports: [
    NgClass,
    TranslatePipe,
    MatTooltipModule,

    IrisDmsDownloadFileButtonComponent,
    IrisDmsUnlinkFileToFileButtonComponent,
  ],
  templateUrl: './dms-file-link-card.component.html',
  styleUrl: './dms-file-link-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisDmsFileLinkCardComponent {
  private readonly fileInfoStore = inject(IrisDmsFileInfoStore);
  private readonly linkedFilesStore = inject(IrisDmsLinkedFilesStore);
  private readonly filesService = inject(IrisFilesService);
  private readonly dmsPermissionsSandbox = inject(IrisDmsPermissionsSandbox);
  private readonly dmsRoutingService = inject(IrisDmsRoutingService);

  readonly linkedFileId = input.required<string>();
  readonly hideActions = input<boolean>(false);

  readonly fileId = this.fileInfoStore.id;
  readonly linkedFile = computed(() => this.linkedFilesStore.getFileById(this.linkedFileId()));
  private readonly linkedFileParentId = computed(() => this.linkedFile()?.parentId);

  readonly linkedFileName = computed(() => this.linkedFile()?.name);
  readonly linkedFilePath = computed(() => this.linkedFile()?.path);
  readonly fileIcon = computed(() => this.filesService.getIconClass(this.linkedFile()));
  readonly fileSourceUrl = computed(() => this.dmsRoutingService.getFileDirectLink(this.linkedFile()));
  readonly folderSourceUrl = computed(() => this.dmsRoutingService.getFolderInternalLink(this.linkedFileParentId()));
  
  readonly hasFolderReadPermission = computed(() => {
    return this.dmsPermissionsSandbox.hasUpdatePermissionForFolderId(this.linkedFileParentId())();
  });
  readonly showDownloadButton = computed(() => !this.hideActions() && this.hasFolderReadPermission());

  private readonly hasUpdatePermission = computed(() => {
    return this.dmsPermissionsSandbox.hasUpdatePermissionForFolderId(this.fileInfoStore.parentId())();
  });
  readonly showUnlinkButton = computed(() => !this.hideActions() && this.hasUpdatePermission());

  readonly actionEffect = effect(() => {
    if (this.linkedFileParentId()) {
      this.dmsPermissionsSandbox.getUserAvailableActions(this.linkedFileParentId());
    }
  });
}
